'use client';

import classnames from 'classnames';
import React, { CSSProperties, SyntheticEvent } from 'react';

import { ButtonStyle } from './ButtonStyle.enum';

export enum ContentAlignment {
    start,
    center,
    end
}

export type ButtonProps = {
    children?: React.ReactNode;
    id?: string;
    className?: string;
    onClick?: (event: SyntheticEvent) => void;
    style?: ButtonStyle | CSSProperties;
    contentAlignment?: ContentAlignment;
    type?: 'button' | 'submit' | 'reset';
    leadingIcon?: false | string | React.JSX.Element;
    trailingIcon?: string | React.JSX.Element;
    disabled?: boolean;
    allowLineBreak?: boolean;
    labelClassName?: string;
    labelUppercase?: boolean;
};

function Button(props: ButtonProps, ref: React.LegacyRef<HTMLButtonElement> | undefined): React.JSX.Element {
    const {
        children,
        id,
        className,
        onClick,
        style,
        contentAlignment,
        type,
        leadingIcon,
        trailingIcon,
        allowLineBreak
    } = props;

    const disabled = props.disabled ?? false;

    let buttonStyle: string, hoverEffect: string;

    switch (style) {
        case ButtonStyle.outlined_on_surface:
            hoverEffect = 'hover:bg-[rgba(var(--on-surface-color-rgb),0.1)]';
            buttonStyle = `border-solid border-2 border-[rgba(var(--on-surface-color-rgb),0.7)] text-[rgba(var(--on-surface-color-rgb),0.7)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.surface:
            hoverEffect = 'hover:bg-[rgba(var(--surface-color-rgb),0.1)]';
            buttonStyle = `bg-[rgba(var(--surface-color-rgb),0.2)] text-[var(--on-surface-color)] ${
                disabled ? '' : hoverEffect
            } py-2 px-32`;
            break;
        case ButtonStyle.delete_action:
            hoverEffect = 'hover:text-darkBlue hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-red text-white ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
        default:
            hoverEffect = 'hover:text-[var(--on-primary-color)] hover:opacity-80';
            buttonStyle = `border-2 border-transparent bg-[var(--primary-color)] text-[var(--on-primary-color)] ${disabled ? '' : hoverEffect} py-2 px-32`;
            break;
    }

    let btnContentAlignment: string;
    switch (contentAlignment) {
        case ContentAlignment.start:
            break;
        case ContentAlignment.end:
            btnContentAlignment = 'justify-end';
            break;
        default:
            btnContentAlignment = 'justify-center';
            break;
    }

    return (
        <button
            id={id}
            className={classnames(
                className,
                buttonStyle,
                disabled ? 'opacity-50 cursor-not-allowed ' : 'active:opacity-50',
                'flex flex-row space-x-8 min-h-[48px]',
                allowLineBreak === true ? 'w-min xl:w-auto' : 'flex-none',
                'outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cyan items-center rounded-4 font-montserrat-bold transition-all duration-150',
                btnContentAlignment,
                props.labelUppercase && 'uppercase'
            )}
            style={typeof style === 'string' ? undefined : style}
            disabled={disabled}
            onClick={(event) => {
                onClick?.(event);
            }}
            type={type}
            ref={ref}>
            {leadingIcon && <div>{leadingIcon}</div>}
            {children && (
                <div
                    className={classnames('flex my-auto', props.labelClassName, {
                        'text-button-14 md:text-button-16': !props.labelClassName
                    })}>
                    {children}
                </div>
            )}
            {trailingIcon && <div>{trailingIcon}</div>}
        </button>
    );
}

/**
 * @deprecated Use `Button` from '@cineamo/ui/src/button/Button'. The apps standardize on `Button`;
 * `NewButton` has no adopters in the cinema app. Kept only for any legacy imports.
 */
export default React.forwardRef(Button);
